import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FalconCloseButton from "components/common/falconCloseButton";
import TemplateRendererSection from "./templateRendererSection";
import axios from "axios";

const TemplatePreviewModal = ({ show, handleClose, templateData, sections, refreshTemplates, readOnly = false }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [sectionContents, setSectionContents] = useState({});
  const [sectionOrder, setSectionOrder] = useState([]);
  const [templateName, setTemplateName] = useState(templateData?.TemplateName || "");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const isEditMode = Boolean(templateData?.TemplateId);

  useEffect(() => {
    if (show) {
      setTemplateName(templateData?.TemplateName || "");
      setIsEditingTitle(false);
      setSectionContents({});
      setSectionOrder([]);
    }
  }, [show, templateData]);

  const toPascalCase = (str) =>
    str
      ?.replace(/(\w)(\w*)/g, (_, first, rest) => first.toUpperCase() + rest.toLowerCase())
      ?.replace(/\s+/g, "");

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error(t("enterTemplateName"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) {
        toast.error(t("customerIdMissing"), {
          position: "top-right",
          autoClose: 3000,
        });
        setIsLoading(false);
        return;
      }

      const isEditMode = Boolean(templateData?.TemplateId);
      const selectedLanguage = templateData?.selectedLanguage || localStorage.getItem("culture");

      const templateId = isEditMode
        ? templateData.TemplateId
        : crypto.randomUUID();

      const pascalCaseWorkType = toPascalCase(templateName);

      const getSectionId = (key) => {
        const sec = sections.find(
          (s) => s.Key === key || s.TemplateSection?.Key === key
        );
        return sec ? sec.SectionId : key;
      };

      // Common section payload preparation
      const placeholderTexts = {
        priceSection: t("priceSectionExample"),
        paymentTermsSection: t("paymentTermsSectionExample"),
        acceptanceSection: t("acceptanceSectionExample"),
        extraContentText: t("extraContentSectionExample"),
        terminationSection: t("terminationSectionExample"),
        introTextBlock: t("introSection"),
        timeScheduleText: t("timeScheduleDescription"),
        proposalTasksBlock: t("proposalTasksExample"),
      };

      const editableKeys = [
        "priceSection",
        "paymentTermsSection",
        "acceptanceSection",
        "extraContentText",
        "terminationSection",
      ];

      const hasRealUserContent = (html, placeholder) => {
        if (!html || !html.trim()) return false;

        if (/<div[^>]*class=["']?placeholder-text["']?[^>]*>/i.test(html)) {
          return false;
        }

        const plain = html
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;/g, "")
          .trim()
          .toLowerCase();

        const placeholderText = (placeholder || "").toLowerCase();

        return plain.length > 3 && !plain.includes(placeholderText);
      };

      const sectionPayloads = sectionOrder
        .filter((key) => key !== "pageBreakAfterExtraContent") // exclude this key
        .map((key, index) => {
          const rawHtml = sectionContents?.[key] || "";
          const placeholder = placeholderTexts[key] || "";

          const shouldSaveContent =
            editableKeys.includes(key) && hasRealUserContent(rawHtml, placeholder);

          return {
            CustomerId: customerId,
            TemplateId: templateId,
            SectionId: getSectionId(key),
            SectionOrder: index + 1,
            DefaultContent: shouldSaveContent ? rawHtml.trim() : "",
            LanguageCode:
              templateData?.LanguageCode ||
              templateData?.selectedLanguage ||
              localStorage.getItem("culture") ||
              "nl",
          };
        });

      //  Create new template (if not in edit mode)
      if (!isEditMode) {
        const templatePayload = {
          TemplateId: templateId,
          CustomerId: customerId,
          TemplateName: templateName,
          WorkType: pascalCaseWorkType,
          CreatedBy: localStorage.getItem("UserId") || null,
          LanguageCode: localStorage.getItem("culture") || null,
        };
        const templateResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/createProposalTemplate`,
          templatePayload,
          {
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );

        if (templateResponse.status === 201 || templateResponse.status === 200) {
          const backendTemplateId = templateResponse.data?.TemplateId;

          for (const payload of sectionPayloads) {
            payload.TemplateId = backendTemplateId;
            await axios.post(
              `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/createProposalTemplateSection`,
              payload,
              {
                headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
              }
            );
          }

          toast.success(t("templateSavedSuccessfully"), {
            position: "top-right",
            autoClose: 3000,
          });

          if (typeof refreshTemplates === "function") {
            refreshTemplates({
              TemplateId: backendTemplateId,
              TemplateName: templateName,
              CreatedBy: localStorage.getItem("UserName") || "--",
              CreatedDate: new Date()
                .toLocaleDateString("en-GB")
                .replace(/\//g, "-"),
            });
          }

          handleClose();
        }
      }
      else {
        const updateResponse = await axios.put(
          `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/updateProposalTemplateSections/${templateId}`,
          {
            sections: sectionPayloads,
            languageCode: selectedLanguage,
          },
          {
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );

        if (updateResponse.status === 200) {
          toast.success(
            t("templateUpdatedSuccessfully"),
            { position: "top-right", autoClose: 3000 }
          );
          handleClose();
        }
      }
    } catch (error) {
      console.error("Error saving/updating template:", error);
      toast.error(t("errorSavingTemplate"), {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setTemplateName(templateData?.TemplateName || "");
    setIsEditingTitle(false);
    setSectionContents({});
    setSectionOrder([]);
    handleClose();
  };

  return (
    <Modal size="lg" show={show} onHide={handleModalClose} backdrop="static">
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center gap-2 w-30">
          <Form.Control
            type="text"
            value={templateName}
            onChange={(e) => !isEditMode && setTemplateName(e.target.value)}
            readOnly={isEditMode}
            disabled={isEditMode}
            className={isEditMode ? "bg-light text-muted" : ""}
            placeholder={t("enterTemplateNameLable")}
          />
        </Modal.Title>
        <FalconCloseButton onClick={handleModalClose} />
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">{t("saving")}</p>
          </div>
        ) : (
          <TemplateRendererSection
            sections={sections || []}
            templateData={templateData}
            onContentChange={(updatedValues) => setSectionContents(updatedValues)}
            onOrderChange={(updatedOrder) => setSectionOrder(updatedOrder)}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="falcon-default"
          size="sm"
          onClick={handleModalClose}
          disabled={isLoading}
        >
          {t("close")}
        </Button>
        {!readOnly && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveTemplate}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t("saving")}
              </>
            ) : (
              t("saveTemplate")
            )}
          </Button>
        )}
      </Modal.Footer>

    </Modal>
  );
};

export default TemplatePreviewModal;